import {faCalendar, faLocationDot, faUtensils} from '@fortawesome/pro-regular-svg-icons';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';

import MagnifyingGlass from '@/assets/icons/magnifying-glass.svg?react';
import {
  type JumpBackInCart,
  type JumpBackInFulfillmentDetail,
  locationLabelKey,
} from '@/components/global/providers/JumpBackInHomepageProvider/jumpBackInCards';
import useCurrencyFormatter from '@/hooks/useCurrencyFormatter';
import useTracking from '@/hooks/useTracking';
import {CATERING_MENU_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {formatEventDate, formatEventTime} from './eventDateTime';
import ResumeCard, {type ResumeCardInfoRow} from './ResumeCard';

type ResumeCartCardProps = {
  cart: JumpBackInCart;
  fulfillmentDetail: JumpBackInFulfillmentDetail;
  index: number;
};

const ResumeCartCard: React.FC<ResumeCartCardProps> = ({cart, fulfillmentDetail, index}) => {
  const {t} = useTranslation('home');
  const {trackClick} = useTracking();
  const currencyFormatter = useCurrencyFormatter();

  const {caterer} = cart;
  const {address, eventName, strategy} = fulfillmentDetail;

  const title = eventName
    ? t('jumpBackInHomepage.cartTitleWithEvent', {eventName})
    : t('jumpBackInHomepage.cartTitle');

  const date = formatEventDate(fulfillmentDetail.eventOn);
  const time = formatEventTime(fulfillmentDetail.eventLocalTime);
  const dateLabel = date && time ? t('jumpBackInHomepage.dateAtTime', {date, time}) : date;

  const itemCount = cart.items.reduce((total, item) => total + item.quantity, 0);
  const imageUrl = caterer.brand?.heroImage?.mobileSearchUrl;

  const infoRows: ResumeCardInfoRow[] = [
    ...(dateLabel ? [{icon: faCalendar, label: dateLabel}] : []),
    {
      icon: faLocationDot,
      label: t(locationLabelKey(strategy), {
        location: address.fullAddress || `${address.city}, ${address.state}`,
      }),
    },
    {
      icon: faUtensils,
      label: t('jumpBackInHomepage.catererWithTotal', {
        caterer: caterer.name,
        total: currencyFormatter(cart.totalDue.subunits, 'USD'),
      }),
    },
  ];

  const href = compilePath(CATERING_MENU_PATH, {catererUrlId: caterer.urlId}, {cartId: cart.id});

  const handleClick = () => {
    trackClick('jump-back-in-homepage-card', {
      misc_json: JSON.stringify({
        card_index: index,
        card_type: 'cart',
        caterer_id: caterer.id,
        caterer_url_id: caterer.urlId,
        cart_id: cart.id,
        fulfillment_detail_id: fulfillmentDetail.id,
      }),
    });
  };

  const image = (
    <div className="relative h-[124px] w-full shrink-0 overflow-hidden rounded-2xl bg-peppercorn-100 tablet:size-[120px]">
      {imageUrl ? (
        <Image
          alt=""
          className="object-cover"
          fill
          sizes="(min-width: 768px) 120px, 100vw"
          src={imageUrl}
        />
      ) : (
        <div className="flex size-full items-center justify-center bg-green-50">
          <MagnifyingGlass aria-hidden className="size-8 text-peppercorn-800" />
        </div>
      )}
      <span className="absolute right-2 top-2 rounded-xl bg-black/60 px-2 py-0.5 text-xs font-semibold text-white">
        {t('jumpBackInHomepage.itemCount', {count: itemCount})}
      </span>
    </div>
  );

  return (
    <ResumeCard href={href} image={image} infoRows={infoRows} onClick={handleClick} title={title} />
  );
};

export default ResumeCartCard;
